// src/components/InlineToolPicker.jsx
import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { buildToolItems } from './tiptap/toolItems';

const { FiX, FiLoader } = FiIcons;

export default function InlineToolPicker({ editor, savedSelection, onClose }) {
  // In the modal, we don’t need to delete anything first — we restore the caret
  const items = useMemo(() => buildToolItems(editor, { deleteRange: false }), [editor]);

  const runWithRestoredCaret = (fn) => {
    if (!editor || !fn) return;
    // restore caret where Plus was clicked
    if (savedSelection?.from != null && savedSelection?.to != null) {
      editor.chain().focus().setTextSelection({ from: savedSelection.from, to: savedSelection.to }).run();
    } else {
      editor.chain().focus().run();
    }
    fn();
    onClose?.(); // close after insert
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
        onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Insert tool</h2>
              <p className="text-sm text-gray-600 mt-1">Same options as “/ → Tools”, plus Attachment.</p>
            </div>
            <button
              onClick={() => onClose?.()}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((it) => (
              <motion.button
                key={it.title}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={[
                  "flex flex-col items-center justify-center p-4 border rounded-lg transition-all",
                  it.locked
                    ? "border-gray-200 bg-gray-50 opacity-75"
                    : "border-gray-200 hover:bg-blue-50 hover:border-blue-200"
                ].join(' ')}
                onClick={() => {
                  if (it.locked) {
                    // Don’t close modal; just trigger upgrade prompt.
                    it.command?.();
                    return;
                  }
                  runWithRestoredCaret(it.command);
                }}
                title={it.description || it.title}
              >
                <SafeIcon
                  icon={it.icon || FiLoader}
                  className={`text-2xl mb-2 ${it.locked ? 'text-gray-400' : 'text-blue-500'}`}
                />
                <span className="text-sm font-medium text-gray-700 text-center">{it.title}</span>
                {it.locked && (
                  <span className="mt-1 text-[11px] text-orange-700 text-center">
                    {it.lockedReason || 'Upgrade to unlock'}
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}